export const WIZARD_STEPS = [
  "Select Trip",
  "Traveler Details",
  "Trip Customization",
  "Review & Payment",
  "Confirm Booking",
];

export const SPECIAL_DETAILS_MAX_LENGTH = 4000;

export const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

export const createEmptyTraveler = (isPrimary = false, expanded = false) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  isPrimary,
  first_name: "",
  middle_name: "",
  last_name: "",
  dateOfBirth: null,
  gender: "",
  nationality: "",
  address: "",
  country: "",
  email: "",
  phoneCountryCode: "",
  phone: "",
  passportNumber: "",
  passportExpiry: null,
  passportIssueDate: null,
  passportLocation: "",
  specialDetails: "",
  accountMode: "",      // "" | "new" | "existing"
  accountId: null,
  expanded: expanded || isPrimary,
  contactMode: "",      // "" | "new" | "existing"  (non-primary travelers only)
  contactId: null,
});

export const PAYMENT_METHODS = [
  { id: "card", label: "Credit / Debit Card" },
  { id: "net_banking", label: "Net Banking" },
  { id: "upi", label: "UPI" },
  { id: "wallet", label: "Wallet" },
];

export const getTripNights = (startDate, endDate, fallbackDuration) => {
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end.getTime() - start.getTime();
    if (!Number.isNaN(diffMs) && diffMs > 0) {
      const nights = Math.round(diffMs / (1000 * 60 * 60 * 24));
      if (nights > 0) return nights;
    }
  }
  const durationMatch = fallbackDuration?.match(/(\d+)\s*Night/i);
  if (durationMatch?.[1]) {
    return Number(durationMatch[1]);
  }
  return 1;
};

export const calculateAddonAmount = (addon, travellers) => {
  if (!addon) return 0;
  const price = Number(addon.addon_price) || 0;
  const basis = (addon.pricing_basis || "per_passenger").toLowerCase();
  if (basis === "fixed") {
    return price;
  }
  return price * (travellers || 0);
};

export const calculateAccommodationAmount = (accommodation, nights, travelerCount = 0) => {
  if (!accommodation) return 0;
  return accommodation.addon.addon_price * Math.max(1, nights || 1) * travelerCount;
};

const toDate = (v, endOfDay = false) => {
  if (!v) return null;
  const d = new Date(`${String(v).slice(0, 10)}T${endOfDay ? "23:59:59" : "00:00:00"}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// -- Offer window (early bird / last minute / group deal) ---------------
export const getOfferWindow = (departure) => {
  if (!departure) return null;
  const candidates = [];
  if (departure.is_early_bird) {
    candidates.push({ type: "early_bird", start: departure.early_bird_start_date, end: departure.early_bird_end_date });
  }
  if (departure.is_last_minute_deal) {
    candidates.push({ type: "last_minute", start: departure.last_minute_start_date, end: departure.last_minute_end_date });
  }
  if (!candidates.length) return null;

  const now = new Date();
  const windows = candidates.map((c) => {
    const start = toDate(c.start);
    const end = toDate(c.end, true);
    const isExpired = Boolean(end) && now > end;
    const isActive = (!start || now >= start) && !isExpired;
    return { type: c.type, start, end, isActive, isExpired };
  });
  return windows.find((w) => w.isActive) || windows[0];
};

// -- High Season Surcharge (HSS) window -----------------------
// `template` here is the resolved HSSTemplate object (fetched via /hss?ids=),
// NOT the departure row, the departure only carries the linked id + its own
// flat charge.
export const getHSSWindow = (template) => {
  if (!template?.valid_from || !template?.valid_until) return null;
  const now = new Date();
  const start = toDate(template.valid_from, false);
  const end = toDate(template.valid_until, true);
  if (!start || !end) return null;
  return {
    start,
    end,
    isActive: now >= start && now <= end,
    isExpired: now > end,
    isUpcoming: now < start,
  };
};

// Resolves the high-season surcharge rule of a departure.
// - Template selected -> its percentage is used (and its date window applies).
// - No template       -> the departure's own manual percentage is used (no window).
// - A fixed amount (hss_amount) is added on top of either.
export const resolveHss = (departure, hssTemplate = null) => {
  if (!departure) return null;
  const amount = parseFloat(departure.hss_amount) || 0;
  if (amount <= 0) return null;

  const hasTemplate = Boolean(departure.hss_template_id ?? departure.hss_template);
  // Window is checked if a template is configured for this departure.
  // If a template is linked, HSS applies ONLY when the template is loaded and its window is active.
  const window = hssTemplate ? getHSSWindow(hssTemplate) : null;
  const isActive = hasTemplate ? Boolean(window?.isActive) : true;

  return {
    amount,                                   // per-person surcharge, shown as-is
    percentage: parseFloat(hssTemplate?.hss_percentage ?? departure.hss_percentage) || 0, // display only
    flatCharge: parseFloat(departure.additional_charges) || 0,                            // display only
    end: window?.end || null,
    isActive,
  };
};

// hss_amount is per person and already includes % and flat charge.
export const computeHssSurcharge = (hss, pax = 1) =>
    hss?.isActive ? Number((hss.amount * pax).toFixed(2)) : 0;